import { Badge, Flex } from "@mantine/core";
import styled from "styled-components";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  ShiftDefinition,
  deleteShiftDefinition,
  getShiftDefinitions,
} from "../../api/shiftDefinitionApi";
import { createNotification } from "../../utils/useNotification";
import TableDeleteButton from "../common/Buttons/TableDeleteButton";
import AreYouSureModal from "../common/Modals/AreYouSureModal";
import TableInfoButton from "../common/Buttons/TableInfoButton";
import DataTable, { Column } from "../common/Table/DataTable";
import { weeklySummary, weeklyWorkHours } from "../../utils/shiftPattern";

/**
 * Vardiya tanımı listesi (plan 2.24). Eskiden backend'de hiç karşılığı olmayan
 * `/weekly-shift` ucuna bakıyordu; artık canlı `/shift-definition/list`.
 *
 * "Klonla" butonu kaldırıldı: eski uçta klonlama tanımı personelleriyle
 * kopyalıyordu, yeni modelde tanım zaten personel taşımıyor — kopyalanacak bir
 * şey kalmadı, yeni tanım açmak aynı işi yapıyor.
 *
 * Kendi tablosunu yazmayı bıraktı, `DataTable`'a geçti: telefonda kart kipi
 * oradan geliyor. Boş durum metni ise DataTable'ın düz `emptyText`'ine
 * SIĞMADIĞI için (ilk kurulum yönlendirmesi, vurgulu örnek içeriyor) erken
 * dönüş olarak korundu.
 */
const WorkShiftTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [tableData, setTableData] = useState<ShiftDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ShiftDefinition | null>(null);

  const getTableData = async () => {
    try {
      const data: any = await getShiftDefinitions({ pageNumber: page, limit });
      setTableData(data?.data ?? []);
      setTotalPages(data?.meta?.totalPages ?? 1);
    } catch {
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    const response: any = await deleteShiftDefinition(itemToDelete._id);
    createNotification({
      success: response?.success,
      message: response?.success ? "Vardiya silindi." : response?.message,
    });
    if (response?.success) await getTableData();
  };

  useEffect(() => {
    getTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  if (isLoading) return null;

  if (tableData.length === 0) {
    return (
      <EmptyState>
        Henüz vardiya tanımlanmamış. "Yeni Vardiya" ile bir çalışma düzeni
        oluşturun (ör. <strong>V1 Gündüz 08:00–16:00</strong>), sonra
        "Personel Atama" ekranından kimlerin bu düzende çalıştığını girin.
      </EmptyState>
    );
  }

  const columns: Column<ShiftDefinition>[] = [
    {
      key: "code",
      header: "Kod",
      width: "10%",
      // Kartta başlık İSİM olmalı: "1" rozeti vardiyayı tanıtmıyor, "Gündüz" tanıtıyor.
      mobile: "meta",
      render: (shift) => (
        <Badge variant="light" color="indigo">
          {shift.code}
        </Badge>
      ),
    },
    { key: "name", header: "Vardiya İsmi", width: "22%", mobile: "title" },
    {
      key: "weekly",
      header: "Haftalık Düzen",
      width: "28%",
      render: (shift) => (
        <span style={{ color: "#4b5563" }}>{weeklySummary(shift.days)}</span>
      ),
    },
    {
      key: "total",
      header: "Toplam",
      width: "10%",
      render: (shift) => (
        <span style={{ color: "#4b5563" }}>
          {weeklyWorkHours(shift.days)} sa
        </span>
      ),
    },
    {
      key: "departments",
      header: "Departman",
      width: "20%",
      render: (shift) => (
        <span style={{ color: "#4b5563" }}>
          {shift.departments && shift.departments.length > 0
            ? shift.departments.map((d) => d.name).join(", ")
            : "Tüm şirket"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "10%",
      headerAlign: "right",
      render: (shift) => (
        <Flex justify="flex-end" onClick={(e) => e.stopPropagation()}>
          <TableInfoButton
            onClick={() => navigate(`${location.pathname}/${shift._id}`)}
          />
          <TableDeleteButton
            onClick={() => {
              setItemToDelete(shift);
              setOpenDeleteModal(true);
            }}
          />
        </Flex>
      ),
    },
  ];

  return (
    <>
      <DataTable<ShiftDefinition>
        columns={columns}
        rows={tableData}
        rowKey={(shift) => shift._id}
        onRowClick={(shift) => navigate(`${location.pathname}/${shift._id}`)}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      <AreYouSureModal
        opened={openDeleteModal}
        setOpened={setOpenDeleteModal}
        proccessFunc={handleDelete}
        title="Vardiyayı Sil"
        text={
          `"${itemToDelete?.name}" vardiyasını silmek istediğinizden emin misiniz? ` +
          "Bu vardiyaya atanmış personel varsa silme işlemi reddedilir."
        }
        confirmButtonText="Sil"
        cancelButtonText="Vazgeç"
      />
    </>
  );
};

export default WorkShiftTable;

const EmptyState = styled.div`
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 32px;
  color: #6b7280;
  font-size: 15px;
  line-height: 22px;
`;

